import config from '@/config';
import axios, { CreateAxiosDefaults } from 'axios';
import { AW_COOKIE, HEADERS } from '@/utilities/constants/metadata';
import { getAWAuthTokens } from '@/utilities/getters/sessionContextGetters';
import { sessionExpirationRequestInterceptor } from '@/utilities/axios/interceptors/sessionExpiredInterceptor';
import { omitCredentialsFetchAdapter } from '@/utilities/axios/browser-fetch-adapter';
import { getMockHarnessHeaders } from '@/utilities/axios/mock-harness-headers';

/**
 * Tells axios' fetch adapter to send the request with `credentials: 'omit'`.
 *
 * `withCredentials` (the XHR flag) only governs *cross-origin* cookies — on a
 * same-origin request the browser attaches `document.cookie` no matter what,
 * so clearing `withCredentials` alone did not stop the cookie jar being sent
 * (CAS-66499: the aggregation calls are same-origin, `*.casino.fanduel.com`).
 * The fetch adapter honours a string credentials mode, and axios passes a
 * string `withCredentials` straight through to `Request.credentials`, so
 * `'omit'` drops cookies even same-origin. Auth then travels solely via the
 * explicit `X-Auth-Token` / `X-Login-Token` headers.
 */
const OMIT_CREDENTIALS = 'omit';

/**
 * Builds the authentication headers Fabric services expect.
 *
 * Only the two session tokens are returned — never the full cookie jar. The
 * previous implementation relied on `withCredentials: true`, which makes the
 * browser attach the entire `document.cookie` to every request. With FanDuel's
 * cookie set that pushed the request headers past Fabric's max header size and
 * the service answered HTTP 400 (CAS-66499). Sending the two tokens explicitly
 * keeps the request small.
 *
 * Tokens missing from the cookies are simply omitted so we never send empty
 * `X-Auth-Token` / `X-Login-Token` headers.
 */
export const getAuthHeaders = (): Record<string, string> => {
  const { authToken, loginToken } = getAWAuthTokens();

  return {
    ...(authToken ? { [AW_COOKIE.AUTH_TOKEN]: authToken } : {}),
    ...(loginToken ? { [AW_COOKIE.LOGIN_TOKEN]: loginToken } : {}),
  };
};

/**
 * Creates a plain axios instance for API calls.
 *
 * Unlike the former `FabricServicesAxios`, this applies no PerimeterX
 * interceptor and sends **no cookies at all**: it forces axios onto its
 * browser fetch adapter with `credentials: 'omit'` (see {@link OMIT_CREDENTIALS}),
 * so the browser never attaches `document.cookie` — even on same-origin
 * requests, which the XHR `withCredentials` flag cannot control. Callers that
 * need authentication use {@link getFabricAuthTokens} (Account UM at call
 * time) or legacy sync {@link getAuthHeaders} from the session blob.
 *
 * @param options - Standard axios instance options. A caller may override
 *   `adapter` / `withCredentials`, but doing so re-enables the cookie jar.
 * @param withSessionInterceptor - When `true`, attaches the custom
 *   session-expiry (HTTP 401) interceptor that refreshes the session and
 *   retries, or clears the session and redirects to login.
 */
export const createApiAxios = (
  options: CreateAxiosDefaults = {},
  withSessionInterceptor: boolean = false,
) => {
  const instance = axios.create({
    // Fetch adapter (not XHR) so credentials: 'omit' is honoured; wrapper
    // strips axios User-Agent so iOS WebKit CORS preflight succeeds.
    adapter: omitCredentialsFetchAdapter,
    // Passed through to fetch as `credentials: 'omit'` — no cookies sent.
    withCredentials: OMIT_CREDENTIALS as unknown as boolean,
    ...options,
    headers: {
      [HEADERS.CACHE_CONTROL]: 'no-store, no-cache',
      ...getMockHarnessHeaders(),
      ...(options.headers ?? {}),
    },
  });

  if (withSessionInterceptor) {
    sessionExpirationRequestInterceptor(instance);
  }

  return instance;
};

export const getFabricEndpoint = (service: string, flow: string) => {
  return config?.fabric?.endpoints[service][flow];
};

export {
  buildFabricAuthHeaders,
  getFabricAuthToken,
  getFabricAuthTokens,
} from './get-fabric-auth-headers';
export type { FabricAuthTokens } from './get-fabric-auth-headers';
